import { BigNumber, utils } from "ethers";
import {
  ADDRESS_MAGNITUDE,
  ERROR_SIGNATURE_BY_SELECTOR,
  KNOWN_REVERT_SELECTORS,
  UNKNOWN_ERROR_DATA_RE,
} from "./errors.constants";

export const formatTokenAmount = (value: BigNumber) => {
  const formatted = utils.formatUnits(value, 18);
  const [integer, fraction = ""] = formatted.split(".");
  const trimmedFraction = fraction.slice(0, 6).replace(/0+$/, "");
  return trimmedFraction ? `${integer}.${trimmedFraction}` : integer;
};

export const formatSignedTokenAmount = (value: BigNumber) => {
  const formatted = utils.formatUnits(value, 18);
  const [integer, fraction = ""] = formatted.split(".");
  const trimmedFraction = fraction.slice(0, 6).replace(/0+$/, "");
  return trimmedFraction ? `${integer}.${trimmedFraction}` : integer;
};

export const humanizeErrorName = (name: string) => (
  name
    .replace(/^GAUGE_CONTROLLER__/, "Gauge controller ")
    .replace(/^VC/, "Voting controller ")
    .replace(/^VE/, "Voting escrow ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim()
);

const formatArgValue = (value: any): string => {
  if (BigNumber.isBigNumber(value)) return value.toString();
  if (Array.isArray(value)) return `[${value.map(formatArgValue).join(", ")}]`;
  if (typeof value === "bigint") return value.toString();
  if (typeof value === "boolean") return value ? "true" : "false";
  if (value === null || value === undefined) return String(value);
  return String(value);
};

const formatParsedArgs = (parsed: any) => {
  const inputs = parsed.errorFragment.inputs || [];
  if (inputs.length === 0) return "";

  return inputs
    .map(
      (input: any, index: number) =>
        `${input.name || `arg${index}`}: ${formatArgValue(
          parsed.args[index]
        )}`
    )
    .join(", ");
};

export const formatDecodedContractError = (
  message: string,
  parsed: any,
  data: string
) => {
  const selector = data.slice(0, 10).toLowerCase();
  const args = formatParsedArgs(parsed);

  return [
    message,
    `Error: ${parsed.signature}`,
    args ? `Args: ${args}` : undefined,
    `Selector: ${selector}`,
    `Raw data: ${data}`,
  ]
    .filter(Boolean)
    .join("\n");
};

export const formatSelectorOnlyContractError = (selector: string, rawData?: string) => {
  const normalized = selector.toLowerCase();
  const knownMessage = KNOWN_REVERT_SELECTORS[normalized];
  const signature = ERROR_SIGNATURE_BY_SELECTOR[normalized];
  const name = signature?.split("(")[0];

  if (!knownMessage && !signature) return undefined;

  return [
    knownMessage ||
      `Contract reverted: ${humanizeErrorName(name || "Unknown error")}.`,
    signature ? `Error: ${signature}` : undefined,
    `Selector: ${normalized}`,
    rawData ? `Raw data: ${rawData}` : undefined,
  ]
    .filter(Boolean)
    .join("\n");
};

const formatErrorArgWord = (word: string): string => {
  let value: BigNumber;
  try {
    value = BigNumber.from(`0x${word}`);
  } catch {
    return `0x${word}`;
  }

  // Top 12 bytes (24 hex chars) empty → fits in an address-sized slot.
  const fitsAddressSlot = /^0{24}/.test(word);
  if (!fitsAddressSlot) return `0x${word}`;

  const decimal = value.toString();
  if (value.gte(ADDRESS_MAGNITUDE)) {
    try {
      return `${decimal} (address ${utils.getAddress(`0x${word.slice(24)}`)})`;
    } catch {
      return decimal;
    }
  }
  return decimal;
};

/**
 * Decode any custom-error revert payload into a clean, human-readable block so
 * the dialog shows a proper error code (and decoded arguments) instead of a raw
 * byte string. Used as a fallback after the signature-aware decoder: it covers
 * selectors we have a canned message for as well as ones we've never seen.
 * Returns undefined for anything that isn't custom-error-shaped data.
 */
export const decodeRawContractError = (data: unknown): string | undefined => {
  if (typeof data !== "string") return undefined;
  const normalized = data.trim();
  if (!UNKNOWN_ERROR_DATA_RE.test(normalized)) return undefined;

  const selector = normalized.slice(0, 10).toLowerCase();
  const words = normalized.slice(10).match(/.{64}/g) || [];
  const values = words.map(formatErrorArgWord);

  const knownMessage = KNOWN_REVERT_SELECTORS[selector];
  const signature = ERROR_SIGNATURE_BY_SELECTOR[selector];
  const headline =
    knownMessage ||
    (signature
      ? `Contract reverted: ${humanizeErrorName(signature.split("(")[0])}.`
      : "The transaction was rejected by the contract.");

  return [
    headline,
    values.length
      ? `Value${values.length > 1 ? "s" : ""}: ${values.join(", ")}`
      : undefined,
    signature ? `Error: ${signature}` : undefined,
    `Error code: ${selector}`,
    `Raw data: ${normalized}`,
  ]
    .filter(Boolean)
    .join("\n");
};
